import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Container, VStack, Heading, Text, Box, Badge, HStack,
  Divider, Button, Link, Table, Thead, Tbody, Tr, Th, Td,
  Code, SimpleGrid, Card, CardBody,
} from "@chakra-ui/react";
import { FiArrowLeft, FiZap } from "react-icons/fi";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";

const AiAssistantCheatsheet: React.FC = () => {
  return (
    <>
      <NavbarWithCallToAction />
      <Container maxW="container.md" py={10}>
        <VStack spacing={6} align="stretch">
          {/* Back link */}
          <Link as={RouterLink} to="/business-builder/cheatsheets" _hover={{ textDecoration: "none" }}>
            <Button variant="ghost" size="sm" leftIcon={<FiArrowLeft />} color="gray.500">
              All Cheatsheets
            </Button>
          </Link>

          {/* Header */}
          <VStack spacing={3} align="start">
            <HStack>
              <Badge colorScheme="purple">AI Assistant</Badge>
              <Text fontSize="xs" color="gray.400">March 2026</Text>
            </HStack>
            <HStack>
              <FiZap color="#9F7AEA" size={24} />
              <Heading size="lg" color="#553C9A">AI Assistant — Quick Reference</Heading>
            </HStack>
            <Text color="gray.600">
              Your AI-powered business automation assistant. Type natural language commands to manage your pipeline,
              clients, emails, proposals, and calendar — without clicking a single button.
            </Text>
          </VStack>

          <Divider />

          {/* How It Works */}
          <Box>
            <Heading size="md" color="#553C9A" mb={3}>How It Works</Heading>
            <VStack align="start" spacing={2}>
              <Text fontSize="sm"><strong>1.</strong> Click the purple lightning bolt button (bottom-right corner)</Text>
              <Text fontSize="sm"><strong>2.</strong> Type what you want to do in plain English</Text>
              <Text fontSize="sm"><strong>3.</strong> The AI reads your data and proposes actions</Text>
              <Text fontSize="sm"><strong>4.</strong> Review the action cards — click the green tick to confirm, red X to reject</Text>
              <Text fontSize="sm"><strong>5.</strong> Records are created in the system. Done.</Text>
            </VStack>
          </Box>

          <Divider />

          {/* Quick Commands */}
          <Box>
            <Heading size="md" color="#553C9A" mb={3}>Quick Commands</Heading>
            <Table variant="simple" size="sm">
              <Thead>
                <Tr><Th>What You Type</Th><Th>What Happens</Th></Tr>
              </Thead>
              <Tbody>
                <Tr>
                  <Td><Code fontSize="xs">Show me my active opportunities</Code></Td>
                  <Td>Lists all active deals with values and stages</Td>
                </Tr>
                <Tr>
                  <Td><Code fontSize="xs">How many deals do I have in the pipeline?</Code></Td>
                  <Td>Pipeline summary with stage breakdown and stale deal count</Td>
                </Tr>
                <Tr>
                  <Td><Code fontSize="xs">Create a new opportunity for John Smith, $5000 website build</Code></Td>
                  <Td>Creates client (if new) + opportunity in one go</Td>
                </Tr>
                <Tr>
                  <Td><Code fontSize="xs">Add a follow-up task to chase the Paradise Lakes deal</Code></Td>
                  <Td>Creates a task on the matched opportunity</Td>
                </Tr>
                <Tr>
                  <Td><Code fontSize="xs">Draft an email to jim@example.com about the proposal</Code></Td>
                  <Td>Creates a DRAFT email (never sends automatically)</Td>
                </Tr>
                <Tr>
                  <Td><Code fontSize="xs">Move the Paradise Lakes deal to NEGOTIATION</Code></Td>
                  <Td>Updates the opportunity stage</Td>
                </Tr>
                <Tr>
                  <Td><Code fontSize="xs">Show me all clients tagged "macushla"</Code></Td>
                  <Td>Lists clients matching the tag</Td>
                </Tr>
                <Tr>
                  <Td><Code fontSize="xs">Schedule a broadcast to all macushla clients for Monday 9am</Code></Td>
                  <Td>Creates a calendar broadcast event that sends at the scheduled time</Td>
                </Tr>
                <Tr>
                  <Td><Code fontSize="xs">Delete the test opportunity I just created</Code></Td>
                  <Td>Permanently removes the record (with confirmation)</Td>
                </Tr>
              </Tbody>
            </Table>
          </Box>

          <Divider />

          {/* Use Cases */}
          <Box>
            <Heading size="md" color="#553C9A" mb={3}>Common Workflows</Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <Card variant="outline">
                <CardBody>
                  <Badge colorScheme="green" mb={2}>New Lead</Badge>
                  <Text fontSize="sm" fontWeight="bold" mb={1}>Email comes in from a new prospect</Text>
                  <Text fontSize="xs" color="gray.500">
                    "Create an opportunity for Sarah Jones, she wants a $3K e-commerce site. Her phone is 0412345678."
                    AI creates the client + opportunity + suggests follow-up tasks.
                  </Text>
                </CardBody>
              </Card>
              <Card variant="outline">
                <CardBody>
                  <Badge colorScheme="blue" mb={2}>Follow Up</Badge>
                  <Text fontSize="sm" fontWeight="bold" mb={1}>Chase a stale deal</Text>
                  <Text fontSize="xs" color="gray.500">
                    "Show me stale deals" then "Draft a follow-up email to the client on the Paradise Lakes deal."
                    AI finds the deal, drafts a professional email, saves as DRAFT for your review.
                  </Text>
                </CardBody>
              </Card>
              <Card variant="outline">
                <CardBody>
                  <Badge colorScheme="purple" mb={2}>Proposal</Badge>
                  <Text fontSize="sm" fontWeight="bold" mb={1}>Generate a proposal</Text>
                  <Text fontSize="xs" color="gray.500">
                    "Create a proposal for the cleaning equipment website deal, $2K upfront plus $500/month."
                    AI generates a full proposal with payment schedule.
                  </Text>
                </CardBody>
              </Card>
              <Card variant="outline">
                <CardBody>
                  <Badge colorScheme="orange" mb={2}>Pipeline Review</Badge>
                  <Text fontSize="sm" fontWeight="bold" mb={1}>Daily pipeline check</Text>
                  <Text fontSize="xs" color="gray.500">
                    "What's my pipeline looking like?" or "Show me deals in the PROPOSAL stage."
                    AI gives you a summary with values, stages, and flags stale deals.
                  </Text>
                </CardBody>
              </Card>
              <Card variant="outline">
                <CardBody>
                  <Badge colorScheme="teal" mb={2}>Broadcast</Badge>
                  <Text fontSize="sm" fontWeight="bold" mb={1}>Send to tagged clients</Text>
                  <Text fontSize="xs" color="gray.500">
                    "Schedule an email broadcast to all clients tagged 'partners' for tomorrow at 3pm about the new feature."
                    AI creates the calendar broadcast event with proper metadata.
                  </Text>
                </CardBody>
              </Card>
              <Card variant="outline">
                <CardBody>
                  <Badge colorScheme="red" mb={2}>Cleanup</Badge>
                  <Text fontSize="sm" fontWeight="bold" mb={1}>Delete test records</Text>
                  <Text fontSize="xs" color="gray.500">
                    "Delete the Anna Spanner opportunity and client."
                    AI deletes the opportunity (+ related tasks) first, then the client. Always asks for confirmation.
                  </Text>
                </CardBody>
              </Card>
            </SimpleGrid>
          </Box>

          <Divider />

          {/* Safety */}
          <Box>
            <Heading size="md" color="#553C9A" mb={3}>Safety & Guardrails</Heading>
            <VStack align="start" spacing={2}>
              <Text fontSize="sm"><strong>Preview before commit</strong> — Every action shows a preview card. Nothing happens until you click confirm.</Text>
              <Text fontSize="sm"><strong>Emails are always DRAFT</strong> — The AI never sends emails directly. Review in the email module first.</Text>
              <Text fontSize="sm"><strong>Deletes require confirmation</strong> — Red action cards with clear warnings. Linked records are checked first.</Text>
              <Text fontSize="sm"><strong>Conversation persists</strong> — Refresh the page and your conversation stays. Click + to start fresh.</Text>
              <Text fontSize="sm"><strong>Feedback</strong> — Thumbs up/down on every response helps improve the AI over time.</Text>
            </VStack>
          </Box>

          <Divider />

          {/* Tips */}
          <Box bg="purple.50" p={5} borderRadius="md" border="1px solid" borderColor="purple.200">
            <Heading size="sm" color="#553C9A" mb={3}>Pro Tips</Heading>
            <VStack align="start" spacing={2}>
              <Text fontSize="sm">Be specific — "Create a $5000 opportunity for John" works better than "add something"</Text>
              <Text fontSize="sm">Include phone/email when creating clients — saves a follow-up step</Text>
              <Text fontSize="sm">Ask for multiple things at once — "Create a client and opportunity for..." does both in one flow</Text>
              <Text fontSize="sm">Use the refine button on action cards to tweak details before confirming</Text>
              <Text fontSize="sm">Context-aware — the AI knows which page you're on. Ask "show me details on this opportunity" when viewing one.</Text>
            </VStack>
          </Box>

          {/* Sign off */}
          <Box>
            <Text>Questions or feedback? Let us know in the chat or email tom@tommillerservices.com.</Text>
            <Text mt={4}>
              Regards,<br />
              <strong>Dr Thomas Miller</strong><br />
              <Text as="em" color="gray.500">Business Builder</Text>
            </Text>
          </Box>
        </VStack>
      </Container>
      <FooterWithFourColumns />
    </>
  );
};

export default AiAssistantCheatsheet;
